import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { MessageCircle, Mic, Volume2, VolumeX } from "lucide-react";
import {
	type FormEvent,
	type KeyboardEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { HttpError, assistant } from "../../lib/api.ts";
import {
	type PrescriptionDraft,
	useAssistantDraftStore,
} from "../../stores/assistant-draft-store.ts";
import { useVoiceCapture } from "./use-voice-capture.ts";
import { useVoiceOutput } from "./use-voice-output.ts";

interface Props {
	onClose: () => void;
}

interface Message {
	role: "user" | "assistant";
	content: string;
	tools?: { name: string; ok: boolean }[];
}

/** Navigation directive a tool can emit for the client to act on. */
interface NavDirective {
	to: string;
	params?: Record<string, string>;
	label?: string;
}

/** Pull the first navigation directive out of a response's tool calls. */
function navDirectiveFrom(
	toolCalls: { output: { ok: boolean; data?: unknown } }[],
): NavDirective | null {
	for (const tc of toolCalls) {
		if (!tc.output.ok) continue;
		const nav = (tc.output.data as { navigation?: NavDirective } | undefined)
			?.navigation;
		if (nav && typeof nav.to === "string") return nav;
	}
	return null;
}

/** Pull the first prescription-draft directive out of a response's tool calls. */
function draftFrom(
	toolCalls: { output: { ok: boolean; data?: unknown } }[],
): PrescriptionDraft | null {
	for (const tc of toolCalls) {
		if (!tc.output.ok) continue;
		const draft = (tc.output.data as { draft?: PrescriptionDraft } | undefined)
			?.draft;
		if (draft && draft.type === "prescription") return draft;
	}
	return null;
}

export function AssistantPanel({ onClose }: Props) {
	const { t, i18n } = useTranslation("assistant");
	const navigate = useNavigate();
	const setPrescriptionDraft = useAssistantDraftStore(
		(s) => s.setPrescriptionDraft,
	);
	const [text, setText] = useState("");
	const [messages, setMessages] = useState<Message[]>([]);
	const [autoSpeak, setAutoSpeak] = useState(true);
	const scrollRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	const status = useQuery({
		queryKey: ["assistant-status"],
		queryFn: assistant.status,
	});

	const aiEnabled = status.data?.ai_enabled;
	const lang = i18n.language.startsWith("ar") ? "ar" : "en";
	const sttLang = lang === "ar" ? "ar-IQ" : "en-US";
	const ttsLang = sttLang;

	// ===== Voice output (TTS) =====
	const voiceOut = useVoiceOutput();

	// ===== Mutation that asks Claude and persists the exchange =====
	const askMutation = useMutation({
		mutationFn: async (userText: string) => {
			const history = messages.map((m) => ({
				role: m.role,
				content: m.content,
			}));
			return assistant.ask(userText, history);
		},
		onSuccess: (result, userText) => {
			setMessages((prev) => [
				...prev,
				{ role: "user", content: userText },
				{
					role: "assistant",
					content: result.response,
					tools: result.tool_calls.map((tc) => ({
						name: tc.name,
						ok: tc.output.ok,
					})),
				},
			]);
			// Speak the response if auto-speak is on
			if (autoSpeak && voiceOut.supported) {
				voiceOut.speak(result.response, ttsLang);
			}
			// A clinical draft (voice → prescription) takes precedence: hand it to
			// the draft store and close so the review modal is visible. The doctor
			// confirms the doses there — nothing is written from voice alone.
			const draft = draftFrom(result.tool_calls);
			if (draft) {
				voiceOut.stop();
				setPrescriptionDraft(draft);
				onClose();
				return;
			}
			// Otherwise, if the assistant navigated us somewhere ("افتح المواعيد /
			// افتح ملف أحمد"), perform the route change and close the panel so the
			// page is visible. The panel is a full-screen overlay, so it must close
			// to reveal the destination.
			const nav = navDirectiveFrom(result.tool_calls);
			if (nav) {
				voiceOut.stop();
				onClose();
				navigate({
					to: nav.to,
					...(nav.params ? { params: nav.params } : {}),
				} as Parameters<typeof navigate>[0]);
			}
		},
	});

	const submit = useCallback(
		(value: string) => {
			const trimmed = value.trim();
			if (!trimmed || askMutation.isPending) return;
			askMutation.mutate(trimmed);
			setText("");
		},
		[askMutation],
	);

	// ===== Voice capture (STT) — auto-submits on final transcript =====
	const voice = useVoiceCapture({
		lang: sttLang,
		onFinal: (transcript) => submit(transcript),
	});

	// Mirror the live transcript into the textarea so users see what was heard
	useEffect(() => {
		if (voice.state === "listening") {
			setText(
				(voice.finalText + (voice.interim ? ` ${voice.interim}` : "")).trim(),
			);
		}
	}, [voice.interim, voice.finalText, voice.state]);

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		submit(text);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			submit(text);
		}
	};

	// Push-to-talk: hold to talk (stop on release), or quick-tap to toggle.
	// A <400ms press is treated as a tap so a stray touch doesn't cut speech
	// off instantly; a tap while listening stops it.
	const pttStartRef = useRef(0);
	const onPttDown = () => {
		if (aiEnabled === false || askMutation.isPending) return;
		if (voice.state === "listening") {
			voice.stop();
			return;
		}
		pttStartRef.current = Date.now();
		voice.start();
	};
	const onPttUp = () => {
		if (
			voice.state === "listening" &&
			Date.now() - pttStartRef.current >= 400
		) {
			voice.stop();
		}
	};

	// Scroll to bottom on new messages + refocus input
	// biome-ignore lint/correctness/useExhaustiveDependencies: messages.length is a trigger dep — it isn't read in the body but must re-run the scroll-to-bottom when a new message arrives
	useEffect(() => {
		scrollRef.current?.scrollTo({
			top: scrollRef.current.scrollHeight,
			behavior: "smooth",
		});
		if (voice.state !== "listening") inputRef.current?.focus();
	}, [messages.length, voice.state]);

	// Close on ESC (but cancel listening first if active)
	useEffect(() => {
		const onEsc = (e: globalThis.KeyboardEvent) => {
			if (e.key !== "Escape") return;
			if (voice.state === "listening") voice.stop();
			else onClose();
		};
		window.addEventListener("keydown", onEsc);
		return () => window.removeEventListener("keydown", onEsc);
	}, [onClose, voice]);

	// Stop any spoken audio when the panel closes
	useEffect(() => () => voiceOut.stop(), [voiceOut]);

	const apiErr =
		askMutation.error instanceof HttpError ? askMutation.error : null;
	const isListening = voice.state === "listening";

	return (
		<div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4">
			<div className="w-full sm:max-w-2xl h-[85dvh] sm:h-[600px] bg-card rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
				{/* Header */}
				<div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
					<div className="flex items-center gap-2">
						<div className="size-9 rounded-full bg-gradient-brand flex items-center justify-center text-white shadow-sm shadow-brand-600/30">
							<Mic className="size-5" strokeWidth={2} aria-hidden />
						</div>
						<div>
							<h3 className="font-bold leading-tight">{t("header.title")}</h3>
							<p className="text-xs text-muted-foreground leading-tight">
								{aiEnabled === false
									? t("header.disabled")
									: aiEnabled === true
										? t("header.subtitle")
										: t("header.loading")}
							</p>
						</div>
					</div>
					<div className="flex items-center gap-1">
						{voiceOut.supported && (
							<button
								type="button"
								onClick={() => {
									setAutoSpeak((v) => !v);
									if (voiceOut.speaking) voiceOut.stop();
								}}
								className={`size-9 rounded-lg flex items-center justify-center transition-colors ${
									autoSpeak
										? "bg-brand-100 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300"
										: "hover:bg-muted text-muted-foreground"
								}`}
								aria-label={t(autoSpeak ? "voice.mute" : "voice.unmute")}
								title={t(autoSpeak ? "voice.mute" : "voice.unmute")}
							>
								{autoSpeak ? (
									<Volume2 className="size-4" strokeWidth={2} aria-hidden />
								) : (
									<VolumeX className="size-4" strokeWidth={2} aria-hidden />
								)}
							</button>
						)}
						<button
							type="button"
							onClick={onClose}
							className="size-9 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground"
							aria-label={t("actions.close")}
						>
							✕
						</button>
					</div>
				</div>

				{/* Messages */}
				<div
					ref={scrollRef}
					className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
				>
					{messages.length === 0 && (
						<div className="text-center pt-8">
							{voice.supported ? (
								<div className="mx-auto mb-4 grid h-28 w-28 place-items-center">
									{isListening ? (
										<Waveform />
									) : (
										<div
											className="size-24 rounded-full animate-breathe"
											style={{
												background:
													"radial-gradient(circle at 30% 30%, #00D4FF, #2F2FBA 55%, #020024)",
											}}
										/>
									)}
								</div>
							) : (
								<MessageCircle
									className="size-12 mx-auto mb-3 text-muted-foreground"
									strokeWidth={1.5}
									aria-hidden
								/>
							)}
							<h4 className="font-semibold mb-1">{t("empty.title")}</h4>
							<p className="text-sm text-muted-foreground mb-1 max-w-md mx-auto">
								{t("empty.subtitle")}
							</p>
							{voice.supported && (
								<p className="text-xs text-brand-600 mb-4 max-w-md mx-auto">
									{t("voice.empty_hint")}
								</p>
							)}
							<div className="flex flex-col gap-2 max-w-md mx-auto">
								{(t("empty.examples", { returnObjects: true }) as string[]).map(
									(e) => (
										<button
											key={e}
											type="button"
											onClick={() => setText(e)}
											className="text-start text-sm px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
										>
											{e}
										</button>
									),
								)}
							</div>
						</div>
					)}

					{messages.map((m, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: append-only chat log, never reordered
						<MessageBubble key={i} msg={m} />
					))}

					{askMutation.isPending && (
						<div className="flex items-center gap-2 text-sm text-muted-foreground px-3 py-2">
							<div className="flex gap-1">
								<span className="size-1.5 rounded-full bg-brand-500 animate-bounce" />
								<span className="size-1.5 rounded-full bg-brand-500 animate-bounce [animation-delay:.15s]" />
								<span className="size-1.5 rounded-full bg-brand-500 animate-bounce [animation-delay:.3s]" />
							</div>
							<span>{t("thinking")}</span>
						</div>
					)}

					{voiceOut.speaking && (
						<div className="flex items-center justify-center gap-2 text-xs text-brand-600 dark:text-brand-400">
							<Volume2 className="size-4" strokeWidth={2} aria-hidden />
							<span>{t("voice.speaking")}</span>
							<button
								type="button"
								onClick={voiceOut.stop}
								className="underline hover:no-underline"
							>
								{t("voice.stop_speaking")}
							</button>
						</div>
					)}

					{apiErr && (
						<div className="rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-400">
							{apiErr.status === 503
								? t("errors.no_api_key")
								: apiErr.error.message || t("errors.generic")}
						</div>
					)}

					{voice.error === "not-supported" && (
						<div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
							{t("voice.unsupported")}
						</div>
					)}
					{voice.error === "not-allowed" && (
						<div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
							{t("voice.permission_denied")}
						</div>
					)}
				</div>

				{/* Input + mic */}
				<form
					onSubmit={handleSubmit}
					className="border-t border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
				>
					<div className="flex items-end gap-2">
						<textarea
							ref={inputRef}
							value={text}
							onChange={(e) => setText(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder={
								isListening ? t("voice.listening") : t("input.placeholder")
							}
							rows={1}
							disabled={aiEnabled === false || isListening}
							className={`flex-1 min-h-[44px] max-h-32 resize-none rounded-lg border bg-card px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 ${
								isListening
									? "border-red-300 dark:border-red-700 bg-red-50/30 dark:bg-red-950/20"
									: "border-border"
							}`}
						/>

						{voice.supported && (
							<button
								type="button"
								onPointerDown={onPttDown}
								onPointerUp={onPttUp}
								onPointerCancel={() =>
									voice.state === "listening" && voice.stop()
								}
								onContextMenu={(e) => e.preventDefault()}
								disabled={aiEnabled === false || askMutation.isPending}
								className={`size-11 shrink-0 rounded-lg flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-none select-none ${
									isListening
										? "bg-red-500 text-white animate-pulse scale-105"
										: "bg-gradient-brand text-white shadow-sm shadow-brand-600/30 hover:scale-105"
								}`}
								aria-label={t(isListening ? "voice.stop" : "voice.start")}
								title={t(isListening ? "voice.stop" : "voice.start")}
							>
								{isListening ? (
									<span aria-hidden className="text-lg">
										⏹
									</span>
								) : (
									<svg
										viewBox="0 0 24 24"
										fill="none"
										className="size-5"
										aria-hidden="true"
										focusable={false}
									>
										<rect
											x="9"
											y="3"
											width="6"
											height="11"
											rx="3"
											fill="currentColor"
										/>
										<path
											d="M5 11a7 7 0 0014 0M12 18v4"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
										/>
									</svg>
								)}
							</button>
						)}

						<button
							type="submit"
							disabled={
								!text.trim() ||
								askMutation.isPending ||
								aiEnabled === false ||
								isListening
							}
							className="size-11 rounded-lg bg-brand-500 hover:bg-brand-600 disabled:bg-gray-300 disabled:dark:bg-gray-700 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
							aria-label={t("input.send")}
						>
							<svg
								viewBox="0 0 24 24"
								fill="none"
								className="size-5"
								aria-hidden="true"
								focusable={false}
							>
								<path
									d="M5 12h14M13 6l6 6-6 6"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
					</div>
					<p className="text-xs text-muted-foreground mt-1.5 text-center">
						{isListening
							? t("voice.listening_hint")
							: voice.supported
								? t("voice.input_hint")
								: t("input.hint")}
					</p>
				</form>
			</div>
		</div>
	);
}

function MessageBubble({ msg }: { msg: Message }) {
	const isUser = msg.role === "user";
	return (
		<div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
			<div
				className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${
					isUser ? "bg-brand-500 text-white" : "bg-muted text-foreground"
				}`}
			>
				{msg.content}
				{msg.tools && msg.tools.length > 0 && (
					<div
						className={`mt-2 pt-2 border-t ${isUser ? "border-white/20" : "border-border"} flex flex-wrap gap-1`}
					>
						{msg.tools.map((tool, i) => (
							<span
								// biome-ignore lint/suspicious/noArrayIndexKey: static tool list rendered once per message
								key={i}
								className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-mono ${
									isUser
										? "bg-card/15"
										: tool.ok
											? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
											: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300"
								}`}
							>
								{tool.ok ? "✓" : "✗"} {tool.name}
							</span>
						))}
					</div>
				)}
			</div>
		</div>
	);
}

/** Animated voice waveform shown while the assistant is listening. */
function Waveform() {
	// 18 bars, each with a pseudo-random height + staggered delay so the row
	// reads as live audio rather than a uniform pulse.
	const bars = Array.from({ length: 18 }, (_, i) => i);
	return (
		<div
			className="flex h-20 items-center justify-center gap-[3px]"
			aria-hidden
		>
			{bars.map((i) => (
				<span
					key={i}
					className="w-1 rounded-full animate-wave"
					style={{
						height: `${30 + ((i * 37) % 60)}%`,
						background: "linear-gradient(180deg, #00D4FF, #2F2FBA)",
						animationDelay: `${(i % 6) * 0.12}s`,
						animationDuration: `${0.8 + ((i * 7) % 5) * 0.1}s`,
					}}
				/>
			))}
		</div>
	);
}
